import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import SalonService from "../../services/SalonService";
import moment from "moment";
import "./AppointmentDetails.css";
import GlobalLoader from "../loadingIndicator/GlobalLoader";
import { toast } from "react-toastify";
import { useSalon } from "../../context/SalonContext";
import {
    FaCalendarAlt, FaClock, FaCut, FaCheckCircle,
    FaStore, FaWallet, FaChevronLeft, FaStar, FaRegStar, FaSpinner
} from "react-icons/fa";

export default function AppointmentDetails() {
    const { id: paramId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { customerId } = useSalon();

    // Use ID from URL params first, fallback to location state
    const apntId = paramId || location.state?.apntId;

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    // Review states
    const [canReview, setCanReview] = useState(false);
    const [reviewUuid, setReviewUuid] = useState(null);
    const [rating, setRating] = useState(5);
    const [reviewText, setReviewText] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isReviewExpanded, setIsReviewExpanded] = useState(false);

    const checkReviewEligibility = useCallback(async () => {
        try {
            const res = await SalonService.canReviewAppointment(customerId, apntId);
            const reviewData = res.data;
            setCanReview(reviewData?.canReview || false);
            if (reviewData?.reviewUuid) {
                setReviewUuid(reviewData.reviewUuid);
                setRating(reviewData.rating || 5);
                setReviewText(reviewData.review || "");
            }
        } catch {
            setCanReview(false);
        }
    }, [customerId, apntId]);

    const fetchDetails = useCallback(async () => {
        try {
            const res = await SalonService.getAppointmentById(apntId);
            const apntData = res.data;
            setData(apntData);

            if (apntData?.apntStatus === "COMPLETED") {
                checkReviewEligibility();
            }
        } catch (e) {
            console.error("Failed to load appointment", e);
        } finally {
            setLoading(false);
        }
    }, [apntId, checkReviewEligibility]);

    useEffect(() => {
        if (apntId) {
            fetchDetails();
        } else {
            setLoading(false);
            toast.error("Appointment ID not found");
        }
    }, [apntId, fetchDetails]);

    const submitReview = async () => {
        if (!reviewText.trim()) {
            toast.warn("Please share your experience in words");
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                salonUuid: data.salonUuid,
                appointmentId: apntId,
                rating,
                review: reviewText.trim(),
            };

            if (reviewUuid) {
                await SalonService.updateReview(customerId, reviewUuid, payload);
            } else {
                await SalonService.createReview(customerId, payload);
            }

            toast.success("Thank you for your feedback!");
            setCanReview(false); // Hide the review section after successful submission
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to submit review");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) return <GlobalLoader />;

    if (!data) return (
        <div className="empty-state-container">
            <p>No appointment details found.</p>
            <button className="btn btn-primary" onClick={() => navigate('/viewBookings')}>
                Back to Appointments
            </button>
        </div>
    );

    const getStatusClass = (status) => {
        return status?.toLowerCase() || 'default';
    };

    const renderReviewSection = () => {
        if (!canReview || data?.apntStatus !== "COMPLETED") return null;

        return (
            <div className="premium-review-card shadow-sm mt-4">
                <div className="review-header" onClick={() => setIsReviewExpanded(!isReviewExpanded)}>
                    <div className="review-title-group">
                        <FaStar className="star-title-icon" />
                        <h4 className="review-title">
                            {reviewUuid ? "Edit Your Review" : "Rate Your Experience"}
                        </h4>
                    </div>
                    {isReviewExpanded ? <FaChevronLeft className="chevron-up" /> : <FaChevronLeft className="chevron-down" />}
                </div>

                {isReviewExpanded && (
                    <div className="review-content">
                        <div className="stars-picker">
                            {[1, 2, 3, 4, 5].map((s) => (
                                <span key={s} onClick={() => setRating(s)} className="star-item">
                                    {s <= rating ? <FaStar className="star-active" /> : <FaRegStar className="star-inactive" />}
                                </span>
                            ))}
                        </div>

                        <textarea
                            className="review-textarea"
                            placeholder="Tell us what you liked or how we can improve..."
                            value={reviewText}
                            onChange={(e) => setReviewText(e.target.value)}
                            rows="3"
                        />

                        <button
                            className="premium-submit-review-btn"
                            disabled={isSubmitting}
                            onClick={submitReview}
                        >
                            {isSubmitting ? <><FaSpinner className="icon-spin me-2" /> Submitting...</> : (reviewUuid ? "Update Review" : "Submit Feedback")}
                        </button>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="details-page-wrapper">
            <div className="details-content-container py-4">
                <div className="premium-details-card shadow-lg">
                    <div className="salon-brand-section">
                        <div className="brand-icon-box">
                            <FaStore />
                        </div>
                        <h2 className="salon-name-display">{data.salonName}</h2>
                        <div className={`status-badge-premium ${getStatusClass(data.apntStatus)}`}>
                            {data.apntStatus}
                        </div>
                    </div>

                    <div className="details-grid-premium">
                        <div className="detail-row-premium">
                            <FaCalendarAlt className="detail-icon-premium" />
                            <div className="detail-info-premium">
                                <span className="detail-label-premium">Date</span>
                                <span className="detail-value-premium">{moment(data.apntDate).format("DD MMM, YYYY")}</span>
                            </div>
                        </div>

                        <div className="detail-row-premium">
                            <FaClock className="detail-icon-premium" />
                            <div className="detail-info-premium">
                                <span className="detail-label-premium">Time</span>
                                <span className="detail-value-premium">{data.apntTime}</span>
                            </div>
                        </div>

                        <div className="detail-row-premium full-width-row">
                            <FaCut className="detail-icon-premium" />
                            <div className="detail-info-premium">
                                <span className="detail-label-premium">Services</span>
                                <span className="detail-value-premium">{data.serviceNames}</span>
                            </div>
                        </div>

                        {data.advanceAmount > 0 && (
                            <div className="detail-row-premium">
                                <FaWallet className="detail-icon-premium" />
                                <div className="detail-info-premium">
                                    <span className="detail-label-premium">Advance Paid</span>
                                    <span className="detail-value-premium highlight">₹{data.advanceAmount}</span>
                                </div>
                            </div>
                        )}

                        {data.verificationNumber && data.verificationNumber !== "null" && (
                            <div className="detail-row-premium">
                                <FaCheckCircle className="detail-icon-premium" />
                                <div className="detail-info-premium">
                                    <span className="detail-label-premium">Verification Code</span>
                                    <span className="detail-value-premium code">{data.verificationNumber}</span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="details-footer-premium">
                        <p className="footer-note-premium">
                            Please show this screen or provide the verification code at the salon.
                        </p>
                    </div>
                </div>

                {renderReviewSection()}

                <div className="action-buttons-group mt-4">
                    <button
                        className="premium-action-btn primary"
                        onClick={() => navigate(`/booking`)}
                    >
                        <FaCalendarAlt className="me-2" /> Book Another Appointment
                    </button>

                    <button
                        className="premium-action-btn secondary"
                        onClick={() => navigate(`/viewBookings`)}
                    >
                        <FaChevronLeft className="me-2" /> View All Bookings
                    </button>
                </div>
            </div>
        </div>
    );
}
